/**
 * OneBase Presupuestos
 * Página de gestión de presupuestos de venta y costo
 */

import React, { useState, useEffect } from 'react';
import {
  presupuestoVentaService,
  presupuestoCostoService,
  proyectoService,
  itemPresupuestoVentaService,
  itemPresupuestoCostoService,
} from '../services/onebaseService';
import { exportTableToPDF, formatCurrencyForPDF } from '../utils/pdfExport';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit, FiTrash2, FiEye, FiDownload, FiFileText, FiCheck, FiX, FiSend } from 'react-icons/fi';
import AgregarItemModal from '../components/AgregarItemModal';
import SeleccionarItemsPresupuestoModal from '../components/SeleccionarItemsPresupuestoModal';
import '../styles/onebase-presupuestos.css';

import Loading from '../components/Loading';
const OneBasePresupuestos = () => {
  const [activeTab, setActiveTab] = useState('venta');
  const [presupuestos, setPresupuestos] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedPresupuesto, setSelectedPresupuesto] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showItemsModal, setShowItemsModal] = useState(false);
  const [showSeleccionarItemsModal, setShowSeleccionarItemsModal] = useState(false);
  const [presupuestoParaCostos, setPresupuestoParaCostos] = useState(null);
  const [formData, setFormData] = useState({
    proyecto: '',
    version: '1',
    estado: 'borrador',
    descripcion: '',
  });

  useEffect(() => {
    cargarDatos();
  }, []);

  useEffect(() => {
    cargarPresupuestos();
  }, [activeTab]);

  const cargarDatos = async () => {
    try {
      const response = await proyectoService.getAll({ status: 'activo' });
      const proyectosData = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setProyectos(proyectosData);
      cargarPresupuestos();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar datos');
    }
  };

  const cargarPresupuestos = async () => {
    setLoading(true);
    try {
      let response;
      if (activeTab === 'venta') {
        response = await presupuestoVentaService.getAll({ status: 'activo' });
      } else {
        response = await presupuestoCostoService.getAll({ status: 'activo' });
      }
      const presupuestosData = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setPresupuestos(presupuestosData);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar presupuestos');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const service = activeTab === 'venta' ? presupuestoVentaService : presupuestoCostoService;

      if (selectedPresupuesto) {
        await service.update(selectedPresupuesto.id, formData);
        toast.success('Presupuesto actualizado');
      } else {
        await service.create(formData);
        toast.success('Presupuesto creado');
      }
      setShowForm(false);
      resetForm();
      cargarPresupuestos();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al guardar presupuesto');
    }
  };

  const handleEdit = (presupuesto) => {
    setSelectedPresupuesto(presupuesto);
    setFormData({
      proyecto: presupuesto.proyecto,
      version: presupuesto.version,
      estado: presupuesto.estado,
      descripcion: presupuesto.descripcion || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Eliminar este presupuesto?')) {
      try {
        const service = activeTab === 'venta' ? presupuestoVentaService : presupuestoCostoService;
        await service.delete(id);
        toast.success('Presupuesto eliminado');
        cargarPresupuestos();
      } catch (error) {
        toast.error('Error al eliminar');
      }
    }
  };

  const handleEnviarAprobacion = async (presupuesto) => {
    try {
      const service = activeTab === 'venta' ? presupuestoVentaService : presupuestoCostoService;
      await service.update(presupuesto.id, { estado: 'enviado_aprobacion_interna' });
      toast.success('Presupuesto enviado a aprobación interna');
      cargarPresupuestos();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al enviar a aprobación');
    }
  };

  const handleAprobar = async (presupuesto) => {
    try {
      const service = activeTab === 'venta' ? presupuestoVentaService : presupuestoCostoService;
      await service.update(presupuesto.id, { estado: 'aprobacion_interna' });
      toast.success('Presupuesto aprobado internamente');
      cargarPresupuestos();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al aprobar presupuesto');
    }
  };

  const handleRechazar = async (presupuesto) => {
    if (window.confirm('¿Rechazar este presupuesto internamente?')) {
      try {
        const service = activeTab === 'venta' ? presupuestoVentaService : presupuestoCostoService;
        await service.update(presupuesto.id, { estado: 'rechazo_interno' });
        toast.success('Presupuesto rechazado internamente');
        cargarPresupuestos();
      } catch (error) {
        console.error('Error:', error);
        toast.error('Error al rechazar presupuesto');
      }
    }
  };

  const handleEnviarAprobacionCliente = async (presupuesto) => {
    try {
      const service = activeTab === 'venta' ? presupuestoVentaService : presupuestoCostoService;
      await service.update(presupuesto.id, { estado: 'enviado_aprobacion_cliente' });
      toast.success('Presupuesto enviado a aprobación del cliente');
      cargarPresupuestos();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al enviar a aprobación del cliente');
    }
  };

  const handleReintentarAprobacionInterna = async (presupuesto) => {
    try {
      const service = activeTab === 'venta' ? presupuestoVentaService : presupuestoCostoService;
      await service.update(presupuesto.id, { estado: 'enviado_aprobacion_interna' });
      toast.success('Presupuesto reenviado a aprobación interna');
      cargarPresupuestos();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al reintentar aprobación');
    }
  };

  const handleAprobarCliente = async (presupuesto) => {
    try {
      const service = activeTab === 'venta' ? presupuestoVentaService : presupuestoCostoService;
      await service.update(presupuesto.id, { estado: 'aprobacion_cliente' });
      toast.success('Presupuesto aprobado por cliente');
      cargarPresupuestos();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al aprobar presupuesto del cliente');
    }
  };

  const handleRechazarCliente = async (presupuesto) => {
    if (window.confirm('¿Rechazar este presupuesto del cliente?')) {
      try {
        const service = activeTab === 'venta' ? presupuestoVentaService : presupuestoCostoService;
        await service.update(presupuesto.id, { estado: 'rechazo_cliente' });
        toast.success('Presupuesto rechazado por cliente');
        cargarPresupuestos();
      } catch (error) {
        console.error('Error:', error);
        toast.error('Error al rechazar presupuesto del cliente');
      }
    }
  };

  const handleAbrirSeleccionItems = (presupuesto) => {
    setPresupuestoParaCostos(presupuesto);
    setShowSeleccionarItemsModal(true);
  };

  const handleCrearPresupuestoCostos = async (itemsSeleccionados) => {
    try {
      if (!presupuestoParaCostos) {
        toast.error('No se encontró el presupuesto de venta');
        return;
      }

      // 1. Crear presupuesto de costos
      const nuevoPresupuestoCosto = {
        proyecto: presupuestoParaCostos.proyecto,
        version: presupuestoParaCostos.version,
        estado: 'borrador',
        descripcion: `Presupuesto de costos para: ${presupuestoParaCostos.descripcion || presupuestoParaCostos.proyecto_nombre}`,
      };

      const presupuestoCostoResponse = await presupuestoCostoService.create(nuevoPresupuestoCosto);
      const presupuestoCostoCreado = presupuestoCostoResponse.data;

      // 2. Crear items del presupuesto de costos solo con los items seleccionados
      if (itemsSeleccionados.length > 0) {
        for (const item of itemsSeleccionados) {
          const nuevoItemCosto = {
            presupuesto_costo: presupuestoCostoCreado.id,
            categoria: item.categoria,
            descripcion: item.descripcion,
            cantidad: item.cantidad_seleccionada || item.cantidad, // Usar cantidad seleccionada
            cantidad_dias: item.cantidad_dias,
            unidad: item.unidad,
            costo_unitario: item.costo_unitario || 0,
            status: 'activo',
          };

          await itemPresupuestoCostoService.create(nuevoItemCosto);
        }
      }

      toast.success(`Presupuesto de costos creado con ${itemsSeleccionados.length} items`);

      // 3. Verificar si quedan cantidades disponibles
      const quedanCantidadesDisponibles = await verificarCantidadesDisponibles(presupuestoParaCostos.id);

      // 4. Cambiar estado según disponibilidad
      if (!quedanCantidadesDisponibles) {
        // Si no quedan cantidades disponibles, marcar como terminado
        await presupuestoVentaService.update(presupuestoParaCostos.id, { estado: 'terminado' });
        toast.info('Presupuesto de venta marcado como terminado - todas las cantidades han sido asignadas');
      }
      // Si quedan cantidades, mantener el estado en 'aprobacion_cliente'

      // 5. Cerrar modal y limpiar estados
      setShowSeleccionarItemsModal(false);
      setPresupuestoParaCostos(null);

      cargarPresupuestos();
      setActiveTab('costo'); // Cambiar a pestaña de costos
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al crear presupuesto de costos');
    }
  };

  const verificarCantidadesDisponibles = async (presupuestoVentaId) => {
    try {
      // 1. Obtener todos los items del presupuesto de venta
      const itemsVentaResponse = await itemPresupuestoVentaService.getAll({
        presupuesto_venta: presupuestoVentaId,
        status: 'activo',
        limit: 1000
      });
      const itemsVenta = Array.isArray(itemsVentaResponse.data)
        ? itemsVentaResponse.data
        : (itemsVentaResponse.data.results || []);

      if (itemsVenta.length === 0) return false;

      // 2. Obtener el proyecto del presupuesto de venta
      const presupuestoVenta = presupuestos.find(p => p.id === presupuestoVentaId) || presupuestoParaCostos;
      if (!presupuestoVenta) return false;

      // 3. Obtener todos los presupuestos de costos del proyecto
      const presupuestosCostoResponse = await presupuestoCostoService.getAll({
        proyecto: presupuestoVenta.proyecto,
        status: 'activo',
        limit: 1000
      });
      const presupuestosCosto = Array.isArray(presupuestosCostoResponse.data)
        ? presupuestosCostoResponse.data
        : (presupuestosCostoResponse.data.results || []);

      // 4. Calcular cantidades asignadas por descripción
      const cantidadesAsignadas = {};
      for (const presupuestoCosto of presupuestosCosto) {
        const itemsCostoResponse = await itemPresupuestoCostoService.getAll({
          presupuesto_costo: presupuestoCosto.id,
          status: 'activo',
          limit: 1000
        });
        const itemsCosto = Array.isArray(itemsCostoResponse.data)
          ? itemsCostoResponse.data
          : (itemsCostoResponse.data.results || []);

        itemsCosto.forEach(itemCosto => {
          const descripcion = itemCosto.descripcion;
          if (!cantidadesAsignadas[descripcion]) {
            cantidadesAsignadas[descripcion] = 0;
          }
          cantidadesAsignadas[descripcion] += parseFloat(itemCosto.cantidad) || 0;
        });
      }

      // 5. Verificar si algún item tiene cantidad disponible
      for (const itemVenta of itemsVenta) {
        const cantidadTotal = parseFloat(itemVenta.cantidad) || 0;
        const cantidadAsignada = cantidadesAsignadas[itemVenta.descripcion] || 0;
        const disponible = cantidadTotal - cantidadAsignada;

        if (disponible > 0.01) { // Margen de tolerancia para decimales
          return true; // Aún quedan cantidades disponibles
        }
      }

      return false; // No quedan cantidades disponibles
    } catch (error) {
      console.error('Error al verificar cantidades disponibles:', error);
      return true; // En caso de error, asumir que quedan cantidades disponibles
    }
  };

  const resetForm = () => {
    setFormData({
      proyecto: '',
      version: '1',
      estado: 'borrador',
      descripcion: '',
    });
    setSelectedPresupuesto(null);
  };

  const filteredPresupuestos = presupuestos.filter((p) =>
    p.proyecto_nombre.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
    }).format(value || 0);
  };

  const estadoColors = {
    // Estados de presupuesto
    borrador: '#9ca3af',
    // Estados de envío - Amarillo
    enviado_aprobacion_interna: '#f59e0b',
    enviado_aprobacion_cliente: '#f59e0b',
    // Estados de aprobación - Verde
    aprobacion_interna: '#10b981',
    aprobacion_cliente: '#10b981',
    en_ejecucion: '#10b981',
    terminado: '#3b82f6', // Azul para terminado
    // Estados de rechazo - Rojo
    rechazo_interno: '#ef4444',
    rechazo_cliente: '#ef4444',
  };

  const estadoLabels = {
    borrador: 'Borrador',
    enviado_aprobacion_interna: 'Enviado a aprobación interna',
    aprobacion_interna: 'Aprobación interna',
    rechazo_interno: 'Rechazo interno',
    enviado_aprobacion_cliente: 'Enviado a aprobación cliente',
    aprobacion_cliente: 'Aprobación cliente',
    rechazo_cliente: 'Rechazo cliente',
    en_ejecucion: 'En ejecución',
    terminado: 'Terminado',
  };

  const handleExportPresupuesto = (presupuesto) => {
    try {
      const data = [presupuesto];
      const columns = [
        { key: 'proyecto_nombre', label: 'Proyecto' },
        { key: 'cliente_nombre', label: 'Cliente' },
        { key: 'version', label: 'Versión' },
        { key: 'estado', label: 'Estado' },
        { key: 'total_presupuesto', label: 'Total' },
      ];
      const filename = `presupuesto_${activeTab}_${presupuesto.proyecto_nombre}_${new Date().toISOString().split('T')[0]}.pdf`;
      exportTableToPDF(`Presupuesto de ${activeTab === 'venta' ? 'Venta' : 'Costo'}`, data, columns, filename);
      toast.success('Presupuesto exportado correctamente');
    } catch (error) {
      console.error('Error al exportar:', error);
      toast.error('Error al exportar presupuesto');
    }
  };

  return (
    <div className="onebase-presupuestos">
      <div className="page-header">
        <h1>Presupuestos</h1>
        <button
          className="btn-primary"
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
        >
          <FiPlus /> Nuevo Presupuesto
        </button>
      </div>

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'venta' ? 'active' : ''}`}
          onClick={() => setActiveTab('venta')}
        >
          Presupuestos de Venta
        </button>
        <button
          className={`tab ${activeTab === 'costo' ? 'active' : ''}`}
          onClick={() => setActiveTab('costo')}
        >
          Presupuestos de Costo
        </button>
      </div>

      <div className="search-bar">
        <input
          type="text"
          placeholder="Buscar por proyecto..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {loading ? (
        <Loading fullScreen={false} />
      ) : filteredPresupuestos.length > 0 ? (
        <div className="presupuestos-table">
          <table>
            <thead>
              <tr>
                <th>Proyecto</th>
                <th>Cliente</th>
                <th>Versión</th>
                <th>Estado</th>
                <th>Total</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredPresupuestos.map((presupuesto) => (
                <tr key={presupuesto.id}>
                  <td>{presupuesto.proyecto_nombre}</td>
                  <td>{presupuesto.cliente_nombre}</td>
                  <td>v{presupuesto.version}</td>
                  <td>
                    <span
                      className="estado-badge"
                      style={{ backgroundColor: estadoColors[presupuesto.estado] }}
                    >
                      {estadoLabels[presupuesto.estado] || presupuesto.estado}
                    </span>
                  </td>
                  <td>{formatCurrency(presupuesto.total_presupuesto)}</td>
                  <td className="acciones">
                    {/* Botones de transición de estado */}
                    {activeTab === 'venta' && (
                      <>
                        {presupuesto.estado === 'borrador' && (
                          <button
                            className="btn-icon success"
                            onClick={() => handleEnviarAprobacion(presupuesto)}
                            title="Enviar a aprobación interna"
                          >
                            <FiSend />
                          </button>
                        )}
                        {presupuesto.estado === 'enviado_aprobacion_interna' && (
                          <>
                            <button
                              className="btn-icon success"
                              onClick={() => handleAprobar(presupuesto)}
                              title="Aprobar"
                            >
                              <FiCheck />
                            </button>
                            <button
                              className="btn-icon danger"
                              onClick={() => handleRechazar(presupuesto)}
                              title="Rechazar"
                            >
                              <FiX />
                            </button>
                          </>
                        )}
                        {presupuesto.estado === 'aprobacion_interna' && (
                          <>
                            <button
                              className="btn-icon success"
                              onClick={() => handleEnviarAprobacionCliente(presupuesto)}
                              title="Enviar a aprobación del cliente"
                            >
                              <FiSend />
                            </button>
                            <button
                              className="btn-icon danger"
                              onClick={() => handleRechazar(presupuesto)}
                              title="Rechazar internamente"
                            >
                              <FiX />
                            </button>
                          </>
                        )}
                        {presupuesto.estado === 'rechazo_interno' && (
                          <button
                            className="btn-icon success"
                            onClick={() => handleReintentarAprobacionInterna(presupuesto)}
                            title="Enviar a aprobación interna"
                          >
                            <FiSend />
                          </button>
                        )}
                        {presupuesto.estado === 'enviado_aprobacion_cliente' && (
                          <>
                            <button
                              className="btn-icon success"
                              onClick={() => handleAprobarCliente(presupuesto)}
                              title="Aprobación cliente"
                            >
                              <FiCheck />
                            </button>
                            <button
                              className="btn-icon danger"
                              onClick={() => handleRechazarCliente(presupuesto)}
                              title="Rechazo cliente"
                            >
                              <FiX />
                            </button>
                          </>
                        )}
                        {presupuesto.estado === 'rechazo_cliente' && (
                          <button
                            className="btn-icon success"
                            onClick={() => handleReintentarAprobacionInterna(presupuesto)}
                            title="Enviar a aprobación interna"
                          >
                            <FiSend />
                          </button>
                        )}
                        {presupuesto.estado === 'aprobacion_cliente' && (
                          <button
                            className="btn-sm btn-success"
                            onClick={() => handleAbrirSeleccionItems(presupuesto)}
                            title="Asignar items a presupuesto de costos"
                            style={{ whiteSpace: 'nowrap' }}
                          >
                            Asignar presupuesto
                          </button>
                        )}
                      </>
                    )}

                    {/* Botones comunes */}
                    <button
                      className="btn-icon"
                      onClick={() => {
                        setSelectedPresupuesto(presupuesto);
                        setShowItemsModal(true);
                      }}
                      title="Agregar/Ver Items"
                    >
                      <FiFileText />
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => handleExportPresupuesto(presupuesto)}
                      title="Exportar PDF"
                    >
                      <FiDownload />
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => handleEdit(presupuesto)}
                      title="Editar"
                    >
                      <FiEdit />
                    </button>
                    <button
                      className="btn-icon danger"
                      onClick={() => handleDelete(presupuesto.id)}
                      title="Eliminar"
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <p>No hay presupuestos</p>
          <button
            className="btn-primary"
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
          >
            Crear presupuesto
          </button>
        </div>
      )}

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedPresupuesto ? 'Editar' : 'Nuevo'} Presupuesto de {activeTab === 'venta' ? 'Venta' : 'Costo'}</h2>
              <button className="close-btn" onClick={() => setShowForm(false)}>
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Proyecto *</label>
                <select
                  name="proyecto"
                  value={formData.proyecto}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Seleccionar...</option>
                  {proyectos.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Versión</label>
                  <input
                    type="number"
                    name="version"
                    value={formData.version}
                    onChange={handleInputChange}
                    min="1"
                  />
                </div>

                <div className="form-group">
                  <label>Estado</label>
                  <select
                    name="estado"
                    value={formData.estado}
                    onChange={handleInputChange}
                  >
                    {activeTab === 'venta' ? (
                      <>
                        <option value="borrador">Borrador</option>
                        <option value="enviado_aprobacion">Enviado a Aprobación</option>
                        <option value="aprobado">Aprobado</option>
                        <option value="rechazado">Rechazado</option>
                      </>
                    ) : (
                      <>
                        <option value="borrador">Borrador</option>
                        <option value="aprobado">Aprobado</option>
                        <option value="en_ejecucion">En Ejecución</option>
                        <option value="cerrado">Cerrado</option>
                      </>
                    )}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Descripción</label>
                <textarea
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleInputChange}
                  rows="3"
                />
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {selectedPresupuesto ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal para agregar items */}
      {selectedPresupuesto && (
        <AgregarItemModal
          presupuesto={selectedPresupuesto}
          isOpen={showItemsModal}
          onClose={() => {
            setShowItemsModal(false);
            setSelectedPresupuesto(null);
          }}
          onItemAdded={async () => {
            // Recargar presupuestos y actualizar el presupuesto seleccionado
            await cargarPresupuestos();
            // Encontrar y actualizar el presupuesto seleccionado con los datos nuevos
            const presupuestosActualizados = activeTab === 'venta'
              ? await presupuestoVentaService.getAll({ status: 'activo' })
              : await presupuestoCostoService.getAll({ status: 'activo' });
            const presupuestosData = Array.isArray(presupuestosActualizados.data)
              ? presupuestosActualizados.data
              : (presupuestosActualizados.data.results || []);
            const presupuestoActualizado = presupuestosData.find(p => p.id === selectedPresupuesto.id);
            if (presupuestoActualizado) {
              setSelectedPresupuesto(presupuestoActualizado);
            }
          }}
          tipoPresupuesto={activeTab}
        />
      )}

      {/* Modal para seleccionar items y crear presupuesto de costos */}
      {presupuestoParaCostos && (
        <SeleccionarItemsPresupuestoModal
          isOpen={showSeleccionarItemsModal}
          onClose={() => {
            setShowSeleccionarItemsModal(false);
            setPresupuestoParaCostos(null);
          }}
          presupuestoVenta={presupuestoParaCostos}
          onItemsSelected={handleCrearPresupuestoCostos}
        />
      )}
    </div>
  );
};

export default OneBasePresupuestos;
